import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../stores/auth'
import { useEvents } from '../stores/events'
import { supabase } from '@unvld/db'
import { formatEUR } from '@unvld/stripe'
import {
  CalendarDays,
  Users,
  Ticket,
  TrendingUp,
  ArrowRight,
  Plus,
  AlertTriangle,
} from 'lucide-react'
import { format, isToday, isTomorrow, addDays, subDays, startOfMonth } from 'date-fns'
import { de } from 'date-fns/locale'

export function DashboardPage() {
  const { org, staff } = useAuth()
  const { events, fetchEvents } = useEvents()

  const [guestsLast30, setGuestsLast30] = useState<number | null>(null)
  const [monthlyRevenue, setMonthlyRevenue] = useState<number | null>(null)

  useEffect(() => {
    fetchEvents({ from_date: new Date().toISOString().split('T')[0], limit: 10 })
  }, [])

  // Fetch 30-day guest count and monthly revenue from Supabase
  useEffect(() => {
    if (!org?.id) return

    const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
    const monthStart = startOfMonth(new Date()).toISOString()

    // Guests in last 30 days: sum of guest_count from non-cancelled tickets
    supabase
      .from('tickets')
      .select('guest_count')
      .eq('org_id', org.id)
      .in('status', ['valid', 'checked_in'])
      .gte('created_at', thirtyDaysAgo)
      .then(({ data }) => {
        if (data) {
          const total = data.reduce((sum, t) => sum + (t.guest_count ?? 1), 0)
          setGuestsLast30(total)
        }
      })

    // Monthly revenue: sum of amount_cents from succeeded payments this month
    supabase
      .from('payments')
      .select('amount_cents')
      .eq('org_id', org.id)
      .eq('status', 'succeeded')
      .gte('created_at', monthStart)
      .then(({ data }) => {
        if (data) {
          const total = data.reduce((sum, p) => sum + (p.amount_cents ?? 0), 0)
          setMonthlyRevenue(total)
        }
      })
  }, [org?.id])

  const todayEvents = events.filter((e) => isToday(new Date(e.date)))
  const upcomingEvents = events.filter((e) => !isToday(new Date(e.date)))

  // Quick stats (from events data)
  const totalCapacity = todayEvents.reduce((sum, e) => sum + e.capacity, 0)

  // Setup warnings
  const warnings: string[] = []
  if (!org?.stripe_onboarding_complete) warnings.push('Stripe-Zahlungen noch nicht eingerichtet')
  if (!org?.logo_url) warnings.push('Club-Logo noch nicht hochgeladen')
  if (events.length === 0) warnings.push('Noch keine Events erstellt')

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-white">
          Hallo, {staff?.display_name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-400 mt-1">
          {org?.name} — {format(new Date(), 'EEEE, d. MMMM yyyy', { locale: de })}
        </p>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-400">Setup noch nicht abgeschlossen</p>
              <ul className="mt-2 space-y-1">
                {warnings.map((w, i) => (
                  <li key={i} className="text-sm text-amber-300/70">• {w}</li>
                ))}
              </ul>
              <Link
                to="/settings"
                className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 mt-2"
              >
                Einstellungen öffnen <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CalendarDays}
          label="Events heute"
          value={String(todayEvents.length)}
          color="indigo"
        />
        <StatCard
          icon={Ticket}
          label="Kapazität heute"
          value={totalCapacity > 0 ? String(totalCapacity) : '—'}
          color="violet"
        />
        <StatCard
          icon={Users}
          label="Gäste (letzte 30T)"
          value={guestsLast30 !== null ? String(guestsLast30) : '—'}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          label="Umsatz (Monat)"
          value={monthlyRevenue !== null ? formatEUR(monthlyRevenue) : '—'}
          color="rose"
        />
      </div>

      {/* Today's Events */}
      {todayEvents.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-white mb-4">Heute</h2>
          <div className="space-y-3">
            {todayEvents.map((event) => (
              <EventRow key={event.id} event={event} highlight />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Kommende Events</h2>
          <Link
            to="/events/new"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neues Event
          </Link>
        </div>
        {upcomingEvents.length > 0 ? (
          <div className="space-y-3">
            {upcomingEvents.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-slate-900 border border-slate-800 rounded-xl">
            <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Noch keine kommenden Events</p>
            <Link
              to="/events/new"
              className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 mt-2 text-sm"
            >
              Erstes Event erstellen <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </section>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, color }: {
  icon: any; label: string; value: string; color: string
}) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-500/10 text-indigo-400',
    violet: 'bg-violet-500/10 text-violet-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    rose: 'bg-rose-500/10 text-rose-400',
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function EventRow({ event, highlight = false }: { event: any; highlight?: boolean }) {
  const statusColors: Record<string, string> = {
    draft: 'bg-slate-700 text-slate-300',
    published: 'bg-emerald-500/20 text-emerald-400',
    sold_out: 'bg-amber-500/20 text-amber-400',
    cancelled: 'bg-red-500/20 text-red-400',
    completed: 'bg-slate-700 text-slate-400',
  }

  const statusLabels: Record<string, string> = {
    draft: 'Entwurf',
    published: 'Live',
    sold_out: 'Ausverkauft',
    cancelled: 'Abgesagt',
    completed: 'Beendet',
  }

  return (
    <Link
      to={`/events/${event.id}`}
      className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
        highlight
          ? 'bg-indigo-600/10 border-indigo-500/30 hover:bg-indigo-600/20'
          : 'bg-slate-900 border-slate-800 hover:border-slate-700'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="text-center min-w-[48px]">
          <p className="text-xs text-slate-400 uppercase">
            {format(new Date(event.date), 'MMM', { locale: de })}
          </p>
          <p className="text-xl font-bold text-white">{format(new Date(event.date), 'd')}</p>
          <p className="text-xs text-slate-500">
            {format(new Date(event.date), 'EEE', { locale: de })}
          </p>
        </div>
        <div>
          <p className="font-medium text-white">{event.title}</p>
          <p className="text-sm text-slate-400">
            {event.doors_open} Uhr • {event.capacity} Plätze
            {event.price_tiers?.[0] && ` • ab ${(event.price_tiers[0].price_cents / 100).toFixed(0)}€`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[event.status] || ''}`}>
          {statusLabels[event.status] || event.status}
        </span>
        <ArrowRight className="w-4 h-4 text-slate-600" />
      </div>
    </Link>
  )
}
